import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, type DocumentSummary } from '../app/api.js'
import { Link } from '../app/Link.js'
import { paths } from '../app/router.js'
import { useDocumentMenu } from '../framework/queries.js'
import { ErrorBlock, Loading } from '../framework/feedback.js'
import { Panel } from '../design/index.js'
import { StarToggle } from '../framework/favourites.js'

const UNGROUPED = 'Transactions'

const groupOf = (item: DocumentSummary): string => item.group ?? UNGROUPED

export const TransactionsHub = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const queryClient = useQueryClient()
  const menu = useDocumentMenu()

  const favourite = useMutation({
    mutationFn: ({ key, on }: { key: string; on: boolean }) => api.setFavourite(key, on),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu', api.companyId] })
      void queryClient.invalidateQueries({ queryKey: ['home', companyId] })
    },
  })

  const groups = useMemo(() => {
    const byGroup = new Map<string, DocumentSummary[]>()
    // A type its company has switched off in Settings is not on the menu.
    for (const item of (menu.data ?? []).filter((d) => d.hidden !== true)) {
      const list = byGroup.get(groupOf(item))
      if (list) list.push(item)
      else byGroup.set(groupOf(item), [item])
    }
    return [...byGroup.entries()]
  }, [menu.data])

  if (menu.isLoading) return <div className="page"><Loading /></div>
  if (menu.error) return <div className="page"><ErrorBlock error={menu.error} /></div>

  return (
    <div className="page">
      <h1 className="page-title">Transactions</h1>
      <p className="page-subtitle">Every document the business raises, grouped by what it is for.</p>

      {groups.map(([group, items]) => (
        <Panel key={group} title={group} aside={`${items.length} document type${items.length === 1 ? '' : 's'}`}>
          <div className="tile-grid">
            {items.map((item) => (
              <div className="tile" key={item.key}>
                <button
                  type="button"
                  className={item.favourite ? 'star-toggle star-toggle--on' : 'star-toggle'}
                  aria-pressed={item.favourite === true}
                  aria-label={item.favourite ? `Remove ${item.labelPlural} from favourites` : `Add ${item.labelPlural} to favourites`}
                  disabled={favourite.isPending}
                  onClick={() => favourite.mutate({ key: item.key, on: item.favourite !== true })}
                >
                  {item.favourite ? '★' : '☆'}
                </button>
                <div className="tile__body">
                  <Link to={paths.list(companyId, item.route)} className="tile__title">{item.labelPlural}</Link>
                  {item.blurb ?? item.description ? <p className="tile__desc">{item.blurb ?? item.description}</p> : null}
                </div>
              </div>
            ))}
            {/*
              Not a document type, but the reference files it here, beside
              Projects and Material Requests (HOME-013).
            */}
            {group === 'Project' ? (
              <div className="tile">
                <StarToggle menuKey="page:drive" label="Project Drive" />
                <div className="tile__body">
                  <Link to={paths.drive(companyId)} className="tile__title">Project Drive</Link>
                  <p className="tile__desc">Add/View the files and collections of a project.</p>
                </div>
              </div>
            ) : null}
          </div>
        </Panel>
      ))}
    </div>
  )
}
